/**
 * Waveform Renderer - Visualizzazione waveform su canvas
 * 
 * @since v3.1.0 - Web Worker integration per Web Vitals optimization
 * @since v3.1.0 - Multiple waveform types support
 */

export class WaveformRenderer {
  constructor(config) {
    this.config = config;
    this.canvas = null;
    this.context = null;
    this.animationFrame = null;
    this.analyser = null;
    this.dataArray = null;
    this.cachedColors = null; // Cache per iOS/Safari
    
    // Web Worker per elaborazione waveform
    this.worker = null;
    this.workerReady = false;
    this.pendingData = null;
    this.previousData = null;
    
    // Waveform type & renderer (lazy loaded)
    this.waveformType = config.advanced?.waveformType || 'bar';
    this.renderer = null;
    this.rendererLoaded = false;
    
    this.setupWebWorker();
  }
  
  /**
   * Setup Web Worker per elaborazione waveform
   * Ottimizza INP spostando elaborazione in background
   */
  setupWebWorker() {
    if (typeof Worker === 'undefined') {
      if (this.config.advanced?.debugMode) {
        console.warn('Web Workers not supported, falling back to main thread');
      }
      return;
    }
    
    try {
      // Inline worker code as Blob per compatibilità con bundler
      const workerCode = this.getWorkerCode();
      const blob = new Blob([workerCode], { type: 'application/javascript' });
      const workerUrl = URL.createObjectURL(blob);
      
      this.worker = new Worker(workerUrl);
      
      this.worker.onmessage = (e) => {
        const { type, data, error } = e.data;
        
        switch (type) {
          case 'ready':
            this.workerReady = true;
            if (this.config.advanced?.debugMode) {
              console.log('🎵 Waveform Worker pronto');
            }
            break;
            
          case 'success':
            this.handleWorkerResult(data);
            break;
            
          case 'error':
            console.error('Waveform Worker error:', error);
            this.fallbackToMainThread();
            break;
        }
      };
      
      this.worker.onerror = (error) => {
        if (this.config.advanced?.debugMode) {
          console.warn('Waveform Worker error, falling back to main thread:', error);
        }
        this.fallbackToMainThread();
      };
      
    } catch (error) {
      if (this.config.advanced?.debugMode) {
        console.warn('Failed to create Waveform Worker, using main thread:', error);
      }
      this.fallbackToMainThread();
    }
  }
  
  /**
   * Restituisce il codice del Web Worker inline
   * Necessario per compatibilità con bundler
   */
  getWorkerCode() {
    return `
      // Waveform Web Worker (inline)
      function processWaveform(audioData, width, height, config = {}) {
        const {
          fftSize = 256,
          smoothing = 0.8,
          barWidth = 2,
          barGap = 1,
          waveformColor = '#00d4ff',
          waveformBgColor = 'rgba(0, 212, 255, 0.1)'
        } = config;
        
        // Limita numero di barre per performance (max 200 barre)
        const maxBars = 200;
        const totalBars = Math.min(Math.floor(width / (barWidth + barGap)), maxBars);
        const samplesPerBar = Math.max(1, Math.floor(audioData.length / totalBars));
        const bars = [];
        
        for (let i = 0; i < totalBars; i++) {
          const start = i * samplesPerBar;
          const end = Math.min(start + samplesPerBar, audioData.length);
          
          let sum = 0;
          for (let j = start; j < end; j++) {
            sum += audioData[j] * audioData[j];
          }
          const rms = Math.sqrt(sum / (end - start));
          const normalized = Math.min(rms * 2, 1);
          const smoothed = normalized * smoothing;
          const barHeight = Math.max(smoothed * height * 0.8, 2);
          
          bars.push({
            x: i * (barWidth + barGap),
            y: height - barHeight,
            width: barWidth,
            height: barHeight,
            intensity: smoothed
          });
        }
        
        return {
          bars,
          totalBars,
          width,
          height,
          color: waveformColor,
          backgroundColor: waveformBgColor,
          timestamp: Date.now()
        };
      }
      
      function processRealTimeData(audioData, previousData = null, config = {}) {
        const { smoothing = 0.8, animationSpeed = 0.1 } = config;
        const currentData = processWaveform(audioData, config.width, config.height, config);
        
        if (previousData && previousData.bars) {
          currentData.bars = currentData.bars.map((bar, index) => {
            const prevBar = previousData.bars[index];
            if (prevBar) {
              const smoothedHeight = prevBar.height * (1 - animationSpeed) + bar.height * animationSpeed;
              return {
                ...bar,
                height: smoothedHeight,
                y: config.height - smoothedHeight
              };
            }
            return bar;
          });
        }
        
        return currentData;
      }
      
      self.addEventListener('message', function(e) {
        const { type, data } = e.data;
        
        try {
          let result;
          
          switch (type) {
            case 'processWaveform':
              result = processWaveform(data.audioData, data.width, data.height, data.config);
              break;
              
            case 'processRealTime':
              result = processRealTimeData(data.audioData, data.previousData, data.config);
              break;
              
            default:
              throw new Error('Unknown message type: ' + type);
          }
          
          self.postMessage({
            type: 'success',
            data: result,
            timestamp: Date.now()
          });
          
        } catch (error) {
          self.postMessage({
            type: 'error',
            error: error.message,
            timestamp: Date.now()
          });
        }
      });
      
      self.postMessage({ type: 'ready', timestamp: Date.now() });
    `;
  }
  
  /**
   * Gestisce risultato dal Web Worker
   */
  handleWorkerResult(data) {
    if (!this.canvas || !this.context) return;
    
    this.renderWaveformFromData(data);
    this.previousData = data;
  }
  
  /**
   * Fallback al thread principale se Web Worker non disponibile
   */
  fallbackToMainThread() {
    this.worker = null;
    this.workerReady = false;
    console.log('🔄 Fallback al thread principale per waveform');
  }
  
  /**
   * Setup canvas
   * @param {HTMLCanvasElement} canvas - Canvas element
   */
  setupCanvas(canvas) {
    if (!canvas) return;
    
    this.canvas = canvas;
    this.context = canvas.getContext('2d');
    
    // Trova il container root per leggere le CSS custom properties
    this.container = canvas.closest('.sasha-container');
    
    // Ottimizzazione per display Retina
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    
    // Limita DPR per performance (max 2x per evitare canvas troppo grandi)
    const effectiveDpr = Math.min(dpr, 2);
    
    canvas.width = rect.width * effectiveDpr;
    canvas.height = rect.height * effectiveDpr;
    
    this.context.scale(effectiveDpr, effectiveDpr);
    
    // Dimensioni logiche
    canvas.style.width = rect.width + 'px';
    canvas.style.height = rect.height + 'px';
    
    if (this.config.advanced?.debugMode) {
      console.log(`🎨 Canvas setup: ${rect.width}x${rect.height} logical, ${canvas.width}x${canvas.height} real (DPR: ${effectiveDpr})`);
    }
  }
  
  /**
   * Setup analizzatore audio
   * @param {AnalyserNode} analyser - Analyser node
   * @param {Uint8Array} dataArray - Data array
   */
  setupAnalyser(analyser, dataArray) {
    this.analyser = analyser;
    this.dataArray = dataArray;
  }
  
  /**
   * Leggi colori da CSS custom properties con fallback multipli (iOS-safe)
   * 
   * IMPORTANTE: I colori provengono SOLO dal CSS del tema.
   * Non ci sono più fallback a configurazione JS.
   * Se un tema non definisce le CSS vars, verranno usati i valori di default
   * definiti nel CSS stesso tramite var(--sasha-waveform-color, defaultValue)
   * 
   * Priorità: CSS vars container > CSS vars host > Errore (tema non completo)
   * 
   * @returns {Object} { waveformColor, waveformBgColor }
   * @throws {Error} Se nessun colore è definito nel tema CSS
   * @since v2.4.0 - Rimosso fallback a config JS
   */
  getColors() {
    if (this.cachedColors) return this.cachedColors;
    
    let waveformColor = null;
    let waveformBgColor = null;
    
    // Prova 1: Leggi dal container (.sasha-container) - tema applicato qui
    if (this.container) {
      const containerStyles = getComputedStyle(this.container);
      const containerWave = containerStyles.getPropertyValue('--sasha-waveform-color').trim();
      const containerWaveBg = containerStyles.getPropertyValue('--sasha-waveform-background-color').trim();
      
      if (containerWave) waveformColor = containerWave;
      if (containerWaveBg) waveformBgColor = containerWaveBg;
    }
    
    // Prova 2: Leggi dall'host element (sasha-player), utile su iOS/Safari
    // Safari propaga meglio le custom properties sull'host del Web Component
    if (!waveformColor || !waveformBgColor) {
      const host = this.canvas.getRootNode()?.host;
      if (host) {
        const hostStyles = getComputedStyle(host);
        const hostWave = hostStyles.getPropertyValue('--sasha-waveform-color').trim();
        const hostWaveBg = hostStyles.getPropertyValue('--sasha-waveform-background-color').trim();
        
        if (!waveformColor && hostWave) waveformColor = hostWave;
        if (!waveformBgColor && hostWaveBg) waveformBgColor = hostWaveBg;
      }
    }
    
    // Validazione: Il tema DEVE definire i colori nel CSS
    if (!waveformColor || !waveformBgColor) {
      console.error('❌ Tema incompleto: mancano --sasha-waveform-color o --sasha-waveform-background-color nel CSS');
      // Valori di emergenza per evitare crash (dovrebbero essere definiti nel tema CSS)
      if (!waveformColor) waveformColor = '#00d4ff';
      if (!waveformBgColor) waveformBgColor = 'rgba(0, 212, 255, 0.1)';
    }
    
    this.cachedColors = { waveformColor, waveformBgColor };
    console.log('🎨 WaveformRenderer colori (dal CSS):', this.cachedColors);
    return this.cachedColors;
  }
  
  /**
   * Inizia rendering waveform
   * Ottimizzato con Web Worker per Web Vitals
   */
  start() {
    if (!this.canvas || !this.context || !this.analyser) return;
    
    // Leggi colori una sola volta all'avvio (cache per performance e iOS compatibility)
    const { waveformColor, waveformBgColor } = this.getColors();
    
    const draw = () => {
      if (!this.analyser || !this.dataArray) return;
      
      this.analyser.getByteFrequencyData(this.dataArray);
      
      const width = this.canvas.clientWidth;
      const height = this.canvas.clientHeight;
      
      // Usa Web Worker se disponibile
      if (this.worker && this.workerReady) {
        this.processWithWorker(this.dataArray, width, height, {
          waveformColor,
          waveformBgColor,
          fftSize: this.config.advanced?.fftSize || 256
        });
      } else {
        // Fallback al thread principale
        this.processInMainThread(this.dataArray, width, height, {
          waveformColor,
          waveformBgColor
        });
      }
      
      this.animationFrame = requestAnimationFrame(draw);
    };
    
    draw();
  }
  
  /**
   * Processa waveform con Web Worker
   * Ottimizza INP spostando elaborazione in background
   */
  processWithWorker(dataArray, width, height, config) {
    // Limita dimensioni per performance (max 1000px width per evitare troppi calcoli)
    const maxWidth = 1000;
    const effectiveWidth = Math.min(width, maxWidth);
    
    // Limita numero di sample per performance
    const maxSamples = 256;
    const step = Math.max(1, Math.floor(dataArray.length / maxSamples));
    
    // Converti Uint8Array a Float32Array per il worker (sample ridotto)
    const sampledLength = Math.floor(dataArray.length / step);
    const audioData = new Float32Array(sampledLength);
    
    for (let i = 0; i < sampledLength; i++) {
      audioData[i] = dataArray[i * step] / 255; // Normalizza
    }
    
    // Invia dati al worker
    this.worker.postMessage({
      type: 'processRealTime',
      data: {
        audioData,
        previousData: this.previousData,
        config: {
          width: effectiveWidth,
          height,
          ...config
        }
      }
    });
  }
  
  /**
   * Processa waveform nel thread principale (fallback)
   */
  processInMainThread(dataArray, width, height, config) {
    // Pulisci canvas
    this.context.clearRect(0, 0, width, height);
    
    // Disegna sfondo
    this.context.fillStyle = config.waveformBgColor;
    this.context.fillRect(0, 0, width, height);
    
    // Disegna barre
    const barCount = Math.min(dataArray.length, 64);
    const barWidth = width / barCount;
    
    this.context.fillStyle = config.waveformColor;
    
    for (let i = 0; i < barCount; i++) {
      const value = dataArray[i];
      const percent = value / 255;
      const barHeight = height * percent;
      const x = i * barWidth;
      const y = height - barHeight;
      
      this.context.fillRect(x, y, barWidth * 0.8, barHeight);
    }
  }
  
  /**
   * Carica renderer per tipo waveform (lazy loading)
   */
  async loadRenderer() {
    if (this.rendererLoaded) return;
    
    try {
      // Carica solo il renderer necessario
      const { getRenderer } = await import('../renderers/WaveformTypes.js');
      this.renderer = getRenderer(this.waveformType);
      this.rendererLoaded = true;
      
      if (this.config.advanced?.debugMode) {
        console.log(`🎨 Waveform renderer '${this.waveformType}' caricato`);
      }
    } catch (error) {
      console.error('Errore caricamento renderer waveform:', error);
      // Fallback al rendering default
      this.rendererLoaded = true;
    }
  }
  
  /**
   * Renderizza waveform dai dati del worker
   */
  async renderWaveformFromData(data) {
    if (!this.canvas || !this.context) return;
    
    // Carica renderer se necessario
    await this.loadRenderer();
    
    const { width, height, bars, backgroundColor, color } = data;
    const colors = {
      waveformColor: color,
      waveformBgColor: backgroundColor
    };
    
    // Usa renderer custom se disponibile
    if (this.renderer) {
      this.renderer(this.context, data, colors);
    } else {
      // Fallback al rendering default (bar)
      this.context.clearRect(0, 0, width, height);
      this.context.fillStyle = backgroundColor;
      this.context.fillRect(0, 0, width, height);
      this.context.fillStyle = color;
      
      bars.forEach(bar => {
        this.context.fillRect(bar.x, bar.y, bar.width, bar.height);
      });
    }
  }
  
  /**
   * Stop rendering
   */
  stop() {
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }
    
    // Pulisci canvas
    if (this.canvas && this.context) {
      const width = this.canvas.clientWidth;
      const height = this.canvas.clientHeight;
      
      this.context.clearRect(0, 0, width, height);
      
      // Usa colori dalla cache
      const { waveformBgColor } = this.getColors();
      
      this.context.fillStyle = waveformBgColor;
      this.context.fillRect(0, 0, width, height);
    }
  }
  
  /**
   * Cleanup
   */
  destroy() {
    this.stop();
    
    // Cleanup Web Worker
    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
      this.workerReady = false;
    }
    
    this.canvas = null;
    this.context = null;
    this.analyser = null;
    this.dataArray = null;
    this.cachedColors = null;
    this.previousData = null;
  }
}

